// 后厨汇总 — the daily central-kitchen production sheet, a faithful digital
// rebuild of the kitchen's handwritten 每日甜品汇总(后厨版). Derived live from
// orders + opening on-hand (现货 = bake-day start = prior close).
//
// Layout mirrors the paper: each store (NY/DT/RH) is its own block of
// 订单[6寸][8寸] · 现 · 总, with a same-structure Total block at the right
// (订6/订8/总现/总数). Three special sub-blocks follow: 5寸 (stores as rows,
// flavors as columns), 蛋挞 (flat 口味+数量), cks (total + parenthesized split).
//
// Print is the primary medium — landscape @page, static big-date header. On a
// phone the 16-column grid can't fit, so it collapses to 4 tabs: NY / DT / RH /
// 合计 (default 合计, the bake-count view the kitchen reads first).
//
// Zero renders BLANK everywhere (rows only exist when active, so blank ≠
// ambiguous). Emphasis numerals are #333 bold on a tan tint — the tan-as-text
// was below WCAG contrast at this size.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as api from '../lib/api.ts';
import { torontoToday } from '../lib/torontoTime.js';
import { isElevated } from '../lib/auth.ts';
import { useIsMobile } from '../lib/useMediaQuery.js';
import { groupKitchenRows } from '../lib/kitchenSheet.js';
import { Toast } from '../components/atoms.jsx';
import { Icon } from '../components/Icon.jsx';

function plusDays(ymd, n) { const [y, m, d] = ymd.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10); }
const n = v => (v ? v : '');   // blank-for-zero

// Pin the Total block by MEASUREMENT: for every .ks-sticky-r cell, right-offset
// = Σ actual widths of the cells to its right in the same row. No hardcoded
// column counts/widths — survives new size columns (10寸), width changes, and
// colSpan rows. Re-measures on data change and window resize.
function usePinTotals(tableRef, deps) {
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const pin = () => {
      for (const row of table.querySelectorAll('tr')) {
        let acc = 0;
        for (let i = row.children.length - 1; i >= 0; i--) {
          const cell = row.children[i];
          if (cell.classList.contains('ks-sticky-r')) cell.style.right = `${acc}px`;
          acc += cell.offsetWidth;
        }
      }
    };
    pin();
    window.addEventListener('resize', pin);
    return () => window.removeEventListener('resize', pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export const KitchenSheet = ({ user }) => {
  const elevated = isElevated(user);
  const isMobile = useIsMobile();
  const [date, setDate] = useState(() => plusDays(torontoToday(), 1));
  const [sheet, setSheet] = useState(null);
  const [toast, setToast] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState('合计');    // mobile: default to the bake-count view

  function reload() { api.loadKitchenSheet(date).then(setSheet).catch(e => setToast({ kind: 'error', msg: e?.message || '加载失败' })); }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [date]);

  const stores = sheet?.stores || ['NY', 'DT', 'RH'];
  const families = useMemo(() => (sheet ? groupKitchenRows(sheet.rows, stores) : []), [sheet, stores]);
  const storeBlocks = families.filter(f => f.kind === 'storeBlock');
  const fiveInch = families.find(f => f.kind === 'flavorCols');   // 5寸
  const flat = families.filter(f => f.kind === 'flat');            // 蛋挞
  const cksFams = families.filter(f => f.kind === 'cks');          // cks

  // Global size columns for the storeBlock table (union across storeBlock
  // families, typically 6寸/8寸). Sizeless flavors span them as one 订 cell.
  const sizeCols = useMemo(() => {
    const rank = ['5寸', '6寸', '8寸', '10寸', '12寸', '其他'];
    const set = new Set();
    for (const f of storeBlocks) for (const s of f.sizes) set.add(s);
    return [...set].sort((a, b) => rank.indexOf(a) - rank.indexOf(b));
  }, [storeBlocks]);
  const nSize = Math.max(sizeCols.length, 1);

  async function confirmAll() {
    try { await api.confirmOrderDay(date); setToast({ kind: 'success', msg: '已确认' }); reload(); }
    catch (e) { setToast({ kind: 'error', msg: e?.message || '确认失败' }); }
  }

  async function exportXlsx() {
    if (!sheet) return;
    setExporting(true);
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(`后厨 ${date}`, { views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }] });
      // storeBlock section: 口味 | per store(订×sizes, 现, 总) | Total(订×sizes, 总现, 总数)
      const perStoreCols = sizeCols.length ? [...sizeCols.map(s => `订${s}`), '现', '总'] : ['订', '现', '总'];
      const head1 = ['口味'], head2 = [''];
      for (const s of stores) { perStoreCols.forEach((c, i) => { head1.push(i === 0 ? s : ''); head2.push(c); }); }
      const totCols = sizeCols.length ? [...sizeCols.map(s => `订${s}`), '总现', '总数'] : ['订', '总现', '总数'];
      totCols.forEach((c, i) => { head1.push(i === 0 ? '合计' : ''); head2.push(c); });
      ws.addRow(head1); ws.addRow(head2);
      for (const fam of storeBlocks) {
        ws.addRow([fam.family]).font = { bold: true };
        for (const fl of fam.flavors) {
          const line = [fl.name];
          for (const s of stores) {
            const cell = fl.perStore[s];
            if (sizeCols.length) for (const sz of sizeCols) line.push(fam.sizes.length ? (cell.bySize[sz] || 0) : (sz === sizeCols[0] ? cell.nullOrders : 0));
            else line.push(cell.nullOrders || cell.total - cell.onHand);
            line.push(cell.onHand, cell.total);
          }
          if (sizeCols.length) for (const sz of sizeCols) line.push(fl.totBySize[sz] || (fam.sizes.length ? 0 : (sz === sizeCols[0] ? fl.totNullOrders : 0)));
          else line.push(fl.totOrdered);
          line.push(fl.totOnHand, fl.totAll);
          ws.addRow(line);
        }
      }
      if (fiveInch) { ws.addRow([]); ws.addRow(['5寸']).font = { bold: true };
        ws.addRow(['门店', ...fiveInch.flavors.map(f => f.name), '合计']);
        for (const s of stores) ws.addRow([s, ...fiveInch.flavors.map(f => f.perStore[s].total || 0), fiveInch.flavors.reduce((a, f) => a + f.perStore[s].total, 0)]);
        ws.addRow(['合计', ...fiveInch.flavors.map(f => f.totAll), fiveInch.flavors.reduce((a, f) => a + f.totAll, 0)]);
      }
      for (const fam of flat) { ws.addRow([]); ws.addRow([fam.family]).font = { bold: true };
        for (const fl of fam.flavors) ws.addRow([fl.name, fl.totAll]);
      }
      for (const fam of cksFams) { ws.addRow([]); ws.addRow([fam.family]).font = { bold: true };
        for (const fl of fam.flavors) ws.addRow([fl.name, fl.totAll, stores.map(s => `${s} ${fl.perStore[s].total}`).join(' / ')]);
      }
      ws.columns.forEach(c => { c.width = 7; }); ws.getColumn(1).width = 16;
      const buf = await wb.xlsx.writeBuffer();
      const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = `vava-后厨汇总-${date}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setToast({ kind: 'error', msg: e?.message || '导出失败' }); }
    finally { setExporting(false); }
  }

  const allConfirmed = sheet?.allConfirmed;
  const isTomorrow = date === plusDays(torontoToday(), 1);

  // storeBlock order cells for one store: a value per size col, or one spanning
  // cell for a sizeless flavor (the 单规 case).
  const orderCells = (fam, cell, keyPrefix) => {
    if (!sizeCols.length) return <td className="ks-num">{n(cell.nullOrders)}</td>;
    if (!fam.sizes.length) return <td className="ks-num ks-single" colSpan={nSize}>{n(cell.nullOrders)}</td>;
    return sizeCols.map(s => <td key={`${keyPrefix}-${s}`} className="ks-num">{n(cell.bySize[s])}</td>);
  };
  // Total-block order cells — sticky-right (part of the pinned 合计 block), with
  // an edge shadow on the leftmost. Mobile passes sticky=false (no pinning).
  const totalOrderCells = (fam, fl, sticky = true) => {
    const sr = sticky ? ' ks-sticky-r' : '';
    const edge = sticky ? ' ks-total-edge' : '';
    if (!sizeCols.length) return <td className={`ks-num ks-emph${sr}${edge}`}>{n(fl.totOrdered)}</td>;
    if (!fam.sizes.length) return <td className={`ks-num ks-emph ks-single${sr}${edge}`} colSpan={nSize}>{n(fl.totOrdered)}</td>;
    return sizeCols.map((s, i) => <td key={`t-${s}`} className={`ks-num ks-emph${sr}${i === 0 ? edge : ''}`}>{n(fl.totBySize[s])}</td>);
  };

  const printDate = useMemo(() => {
    const [y, m, d] = date.split('-');
    const mon = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][Number(m) - 1];
    return `${mon} ${Number(d)} ${y}`;
  }, [date]);

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">后厨汇总</h1>
          <div className="vava-page-meta">订单为次日预订 · 现货取 {sheet?.prevClose || '前一日'} 收市库存 · 合计 = 总订 + 总现</div>
        </div>
        <div className="ks-tools">
          <input type="date" className="oe-date" value={date} onChange={e => setDate(e.target.value)} aria-label="生产日" />
          {isTomorrow && <span className="oe-tag">明天</span>}
          <button className="btn btn-ghost btn-sm" onClick={() => window.print()}><Icon name="print" /> 打印</button>
          <button className="btn btn-ghost btn-sm" onClick={exportXlsx} disabled={exporting}>{exporting ? '导出中…' : <><Icon name="download" /> Excel</>}</button>
        </div>
      </div>

      {/* print-only header: static big date + generated time, like the paper sheet */}
      <div className="ks-print-head">
        <span className="ks-print-title">每日甜品汇总 · 后厨版</span>
        <span className="ks-print-date">{printDate}</span>
        <span className="ks-print-meta">现货取 {sheet?.prevClose} 收市 · 三店 {stores.join(' / ')} · 生成 {new Date().toLocaleString('zh-CN', { hour12: false }).slice(0, 16)}</span>
      </div>

      {/* confirm banner */}
      <div className={`ks-banner ${allConfirmed ? 'is-ok' : 'is-warn'}`} role="status">
        {allConfirmed
          ? <span><Icon name="check" /> 订单已确认 · 三店</span>
          : <span><Icon name="alert" /> 未确认 · 仅现货 — {stores.filter(s => !sheet?.confirmed?.[s]).join(' / ')} 尚未确认</span>}
        {!allConfirmed && elevated && <button className="btn btn-primary btn-sm" onClick={confirmAll}>确认三店</button>}
      </div>

      {!sheet ? <div className="vava-card empty-state"><div className="title">加载中…</div></div>
       : families.length === 0 ? (
        <div className="vava-card empty-state"><div className="emoji">🍰</div><div className="title">{allConfirmed ? '今日无预订、无备货' : '暂无数据'}</div></div>
       ) : isMobile ? (
        <MobileSheet {...{ stores, tab, setTab, storeBlocks, sizeCols, fiveInch, flat, cksFams, totalOrderCells, orderCells }} />
       ) : (
        <DesktopSheet {...{ stores, storeBlocks, sizeCols, nSize, fiveInch, flat, cksFams, orderCells, totalOrderCells }} />
       )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
    </>
  );
};

// ---------- desktop: per-store blocks + sticky-right Total + sub-blocks --------

function DesktopSheet({ stores, storeBlocks, sizeCols, nSize, fiveInch, flat, cksFams, orderCells, totalOrderCells }) {
  const storeSpan = nSize + 2;   // 订(×sizes) + 现 + 总 per store
  const gridRef = useRef(null);
  usePinTotals(gridRef, [storeBlocks, sizeCols]);
  return (
    <div className="vava-card ks-card ks-scroll">
      {storeBlocks.length > 0 && (
        <table className="ks-table ks-grid" ref={gridRef}>
          <thead>
            <tr className="ks-head-stores">
              <th rowSpan={2} className="ks-name" scope="col">口味</th>
              {stores.map(s => <th key={s} colSpan={storeSpan} className="ks-store-h" scope="colgroup">{s}</th>)}
              <th colSpan={nSize + 2} className="ks-total-h ks-sticky-r" scope="colgroup">合计</th>
            </tr>
            <tr className="ks-head-sub">
              {stores.map(s => (
                <SubHead key={s} sizeCols={sizeCols} />
              ))}
              {sizeCols.length ? sizeCols.map((s, i) => <th key={`t${s}`} className={`ks-sub ks-sticky-r${i === 0 ? ' ks-total-edge' : ''}`} scope="col">订{s}</th>) : <th className="ks-sub ks-sticky-r ks-total-edge" scope="col">订</th>}
              <th className="ks-sub ks-sticky-r" scope="col">总现</th>
              <th className="ks-sub ks-sticky-r ks-emph" scope="col">总数</th>
            </tr>
          </thead>
          <tbody>
            {storeBlocks.map(fam => (
              <FamilyRows key={fam.family} family={fam.family} span={1 + stores.length * storeSpan + nSize + 2}>
                {fam.flavors.map(fl => (
                  <tr key={fl.key}>
                    <th className="ks-name" scope="row">{fl.name}</th>
                    {stores.map(s => {
                      const cell = fl.perStore[s];
                      return (
                        <Frag key={s}>
                          {orderCells(fam, cell, s)}
                          <td className="ks-num ks-muted">{n(cell.onHand)}</td>
                          <td className="ks-num">{n(cell.total)}</td>
                        </Frag>
                      );
                    })}
                    {totalOrderCells(fam, fl)}
                    <td className="ks-num ks-muted ks-sticky-r">{n(fl.totOnHand)}</td>
                    <td className="ks-num ks-emph ks-total-cell ks-sticky-r">{n(fl.totAll)}</td>
                  </tr>
                ))}
              </FamilyRows>
            ))}
          </tbody>
        </table>
      )}

      {fiveInch && <FiveInchTable stores={stores} fam={fiveInch} />}
      {flat.map(fam => <FlatTable key={fam.family} fam={fam} />)}
      {cksFams.map(fam => <CksBlock key={fam.family} fam={fam} stores={stores} />)}
    </div>
  );
}

function SubHead({ sizeCols }) {
  return (<>
    {sizeCols.length ? sizeCols.map(s => <th key={s} className="ks-sub" scope="col">订{s}</th>) : <th className="ks-sub" scope="col">订</th>}
    <th className="ks-sub" scope="col">现</th>
    <th className="ks-sub" scope="col">总</th>
  </>);
}

// 5寸 — stores as rows, flavors as (dynamic) columns.
function FiveInchTable({ stores, fam }) {
  const rowTotal = s => fam.flavors.reduce((a, f) => a + f.perStore[s].total, 0);
  return (
    <table className="ks-table ks-sub-table">
      <thead>
        <tr className="ks-cat"><td colSpan={fam.flavors.length + 2}>5寸</td></tr>
        <tr className="ks-head-sub">
          <th className="ks-name" scope="col">门店</th>
          {fam.flavors.map(f => <th key={f.key} className="ks-sub" scope="col">{f.name}</th>)}
          <th className="ks-sub ks-emph" scope="col">合计</th>
        </tr>
      </thead>
      <tbody>
        {stores.map(s => (
          <tr key={s}>
            <th className="ks-name" scope="row">{s}</th>
            {fam.flavors.map(f => <td key={f.key} className="ks-num">{n(f.perStore[s].total)}</td>)}
            <td className="ks-num ks-emph">{n(rowTotal(s))}</td>
          </tr>
        ))}
        <tr className="ks-foot-row">
          <th className="ks-name" scope="row">合计</th>
          {fam.flavors.map(f => <td key={f.key} className="ks-num ks-emph">{n(f.totAll)}</td>)}
          <td className="ks-num ks-emph ks-total-cell">{n(fam.flavors.reduce((a, f) => a + f.totAll, 0))}</td>
        </tr>
      </tbody>
    </table>
  );
}

// 蛋挞 — flat 口味 + 数量.
function FlatTable({ fam }) {
  return (
    <table className="ks-table ks-sub-table ks-flat">
      <thead>
        <tr className="ks-cat"><td colSpan={2}>{fam.family}</td></tr>
      </thead>
      <tbody>
        {fam.flavors.map(fl => (
          <tr key={fl.key}>
            <th className="ks-name" scope="row">{fl.name}</th>
            <td className="ks-num ks-emph">{n(fl.totAll)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// cks — total + parenthesized per-store split, matching the handwritten sheet.
function CksBlock({ fam, stores }) {
  return (
    <div className="ks-cks">
      <div className="ks-cat ks-cks-head">{fam.family}</div>
      {fam.flavors.map(fl => (
        <div className="ks-cks-row" key={fl.key}>
          <span className="ks-cks-name">{fl.name}</span>
          <span className="ks-cks-total">{fl.totAll}</span>
          <span className="ks-cks-split">（{stores.map(s => `${s} ${fl.perStore[s].total}`).join(' / ')}）</span>
        </div>
      ))}
    </div>
  );
}

// ---------- mobile: NY / DT / RH / 合计 tabs -----------------------------------

function MobileSheet({ stores, tab, setTab, storeBlocks, sizeCols, fiveInch, flat, cksFams, totalOrderCells, orderCells }) {
  const tabs = [...stores, '合计'];
  return (
    <div className="vava-card ks-card">
      <div className="ks-tabs" role="tablist">
        {tabs.map(t => (
          <button key={t} role="tab" aria-selected={tab === t} className={`ks-tab${tab === t ? ' is-active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>
      {tab === '合计' ? (
        <table className="ks-table ks-mobile">
          <thead>
            <tr>
              <th scope="col">口味</th>
              {sizeCols.length ? sizeCols.map(s => <th key={s} scope="col">订{s}</th>) : <th scope="col">订</th>}
              <th scope="col">总现</th>
              <th scope="col" className="ks-emph">总数</th>
            </tr>
          </thead>
          <tbody>
            {storeBlocks.map(fam => (
              <FamilyRows key={fam.family} family={fam.family} span={2 + Math.max(sizeCols.length, 1) + 1}>
                {fam.flavors.map(fl => (
                  <tr key={fl.key}>
                    <th className="ks-name" scope="row">{fl.name}</th>
                    {totalOrderCells(fam, fl, false)}
                    <td className="ks-num ks-muted">{n(fl.totOnHand)}</td>
                    <td className="ks-num ks-emph ks-total-cell">{n(fl.totAll)}</td>
                  </tr>
                ))}
              </FamilyRows>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="ks-table ks-mobile">
          <thead>
            <tr>
              <th scope="col">口味</th>
              {sizeCols.length ? sizeCols.map(s => <th key={s} scope="col">订{s}</th>) : <th scope="col">订</th>}
              <th scope="col">现</th>
              <th scope="col" className="ks-emph">总</th>
            </tr>
          </thead>
          <tbody>
            {storeBlocks.map(fam => (
              <FamilyRows key={fam.family} family={fam.family} span={2 + Math.max(sizeCols.length, 1) + 1}>
                {fam.flavors.map(fl => {
                  const cell = fl.perStore[tab];
                  return (
                    <tr key={fl.key}>
                      <th className="ks-name" scope="row">{fl.name}</th>
                      {orderCells(fam, cell, tab)}
                      <td className="ks-num ks-muted">{n(cell.onHand)}</td>
                      <td className="ks-num ks-emph">{n(cell.total)}</td>
                    </tr>
                  );
                })}
              </FamilyRows>
            ))}
          </tbody>
        </table>
      )}
      {/* sub-blocks live on the 合计 tab only (they're global, not per-store) */}
      {tab === '合计' && (
        <div className="ks-mobile-subs">
          {fiveInch && <FiveInchTable stores={stores} fam={fiveInch} />}
          {flat.map(fam => <FlatTable key={fam.family} fam={fam} />)}
          {cksFams.map(fam => <CksBlock key={fam.family} fam={fam} stores={stores} />)}
        </div>
      )}
    </div>
  );
}

// Family section header row + its flavor rows.
function FamilyRows({ family, span, children }) {
  return (<>
    <tr className="ks-cat"><td colSpan={span}>{family}</td></tr>
    {children}
  </>);
}
function Frag({ children }) { return <>{children}</>; }
